/**
 * Audio Visualizer
 *
 * Animated canvas component showing real-time audio frequency visualization.
 * - Idle: Breathing circle (slate)
 * - Connecting: Rotating ring (cyan)
 * - Connected/Ready: Breathing cyan ring
 * - AIDA speaking: Radial frequency bars (cyan gradient)
 *
 * Performance optimizations:
 * - Pre-allocated Uint8Array buffer (no per-frame GC pressure)
 * - 30fps throttle (33ms frame budget)
 * - Mobile shadow disable (touch detection)
 * - requestAnimationFrame(draw) as initial call instead of draw()
 *
 * Adapted from AIDA's visualizer with gradient colors (emerald -> cyan -> blue).
 */

import React, { useRef, useEffect } from 'react';

interface AudioVisualizerProps {
  analyser: AnalyserNode | null;
  inputLevel?: number;
  isConnected: boolean;
  isConnecting?: boolean;
  size?: 'small' | 'medium' | 'large';
  className?: string;
}

// Colors
const CYAN = '#06B6D4';
const EMERALD = '#34d399';
const BLUE = '#3b82f6';
const SLATE = '#334155';
const EMERALD_DIM = 'rgba(52, 211, 153, 0.38)';
const CYAN_DIM = 'rgba(6, 182, 212, 0.4)';
const USER_INPUT_THRESHOLD = 0.01;

const AudioVisualizer: React.FC<AudioVisualizerProps> = ({
  analyser,
  inputLevel = 0,
  isConnected,
  isConnecting = false,
  size = 'medium',
  className = '',
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationRef = useRef<number>(0);

  // Size configurations
  const sizeConfig = {
    small: { height: 128, baseRadiusRatio: 0.25, maxBarRatio: 0.20 },
    medium: { height: 200, baseRadiusRatio: 0.20, maxBarRatio: 0.25 },
    large: { height: 280, baseRadiusRatio: 0.18, maxBarRatio: 0.28 },
  };

  const config = sizeConfig[size];

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Set canvas dimensions with DPI scaling
    const resizeCanvas = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const rect = canvas.getBoundingClientRect();

      // Set actual size in memory (scaled to account for extra pixel density)
      canvas.width = rect.width * dpr;
      canvas.height = rect.height * dpr;

      // Normalize coordinate system to use css pixels
      ctx.scale(dpr, dpr);
    };

    // Initial resize
    resizeCanvas();

    // Handle window resize
    window.addEventListener('resize', resizeCanvas);

    let dataArray = new Uint8Array(0);
    const FRAME_INTERVAL = 33;
    let lastFrameTime = 0;
    const isMobile =
      (typeof window !== 'undefined' && 'ontouchstart' in window) ||
      (typeof navigator !== 'undefined' && navigator.maxTouchPoints > 0);

    const drawMirroredBars = (
      centerX: number,
      centerY: number,
      baseRadius: number,
      maxBarHeight: number,
      barsToDraw: number,
      valueForIndex: (index: number) => number,
      strokeStyle: string | CanvasGradient,
      shadowColor: string,
      shadowBlur: number
    ) => {
      ctx.shadowBlur = isMobile ? 0 : shadowBlur;
      ctx.shadowColor = shadowColor;
      ctx.strokeStyle = strokeStyle;
      ctx.lineWidth = 3;
      ctx.lineCap = 'round';

      const angleStep = Math.PI / barsToDraw;

      for (let i = 0; i < barsToDraw; i++) {
        const percent = Math.max(0, Math.min(1, valueForIndex(i)));
        const barHeight = maxBarHeight * (percent * percent + 0.05);

        const angleRight = -Math.PI / 2 + (i * angleStep);
        const angleLeft = -Math.PI / 2 - (i * angleStep);

        const rX1 = centerX + Math.cos(angleRight) * baseRadius;
        const rY1 = centerY + Math.sin(angleRight) * baseRadius;
        const rX2 = centerX + Math.cos(angleRight) * (baseRadius + barHeight);
        const rY2 = centerY + Math.sin(angleRight) * (baseRadius + barHeight);

        const lX1 = centerX + Math.cos(angleLeft) * baseRadius;
        const lY1 = centerY + Math.sin(angleLeft) * baseRadius;
        const lX2 = centerX + Math.cos(angleLeft) * (baseRadius + barHeight);
        const lY2 = centerY + Math.sin(angleLeft) * (baseRadius + barHeight);

        ctx.beginPath();
        ctx.moveTo(rX1, rY1);
        ctx.lineTo(rX2, rY2);
        ctx.stroke();

        ctx.beginPath();
        ctx.moveTo(lX1, lY1);
        ctx.lineTo(lX2, lY2);
        ctx.stroke();
      }
    };

    const draw = (timestamp: number) => {
      if (!canvas) return;

      if (timestamp - lastFrameTime < FRAME_INTERVAL) {
        animationRef.current = requestAnimationFrame(draw);
        return;
      }
      lastFrameTime = timestamp;

      // Get dimensions in logical CSS pixels
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const width = canvas.width / dpr;
      const height = canvas.height / dpr;
      const centerX = width / 2;
      const centerY = height / 2;

      // Clear canvas
      ctx.clearRect(0, 0, width, height);

      // Configuration
      const minDimension = Math.min(width, height);
      const baseRadius = minDimension * config.baseRadiusRatio;
      const maxBarHeight = minDimension * config.maxBarRatio;

      if (isConnecting) {
        // --- CONNECTING STATE: Rotating ring ---
        const time = Date.now() * 0.003;
        const rotationAngle = time % (2 * Math.PI);

        ctx.save();
        ctx.translate(centerX, centerY);
        ctx.rotate(rotationAngle);

        // Draw partial ring
        ctx.beginPath();
        ctx.arc(0, 0, baseRadius, 0, Math.PI * 1.5);
        ctx.lineWidth = 3;
        ctx.strokeStyle = CYAN;
        ctx.lineCap = 'round';
        ctx.stroke();

        ctx.restore();

        animationRef.current = requestAnimationFrame(draw);
        return;
      }

      if (!isConnected || !analyser) {
        // --- IDLE STATE: Breathing Circle ---
        const time = Date.now() * 0.002;
        // Breathing effect: radius fluctuates slightly
        const pulse = Math.sin(time) * 4;
        const idleRadius = baseRadius + pulse;

        // Draw breathing ring
        ctx.beginPath();
        ctx.arc(centerX, centerY, idleRadius, 0, 2 * Math.PI);
        ctx.lineWidth = 2;
        ctx.strokeStyle = SLATE;
        ctx.shadowBlur = 0;
        ctx.stroke();

        // Draw faint inner ring
        ctx.beginPath();
        ctx.arc(centerX, centerY, idleRadius * 0.7, 0, 2 * Math.PI);
        ctx.lineWidth = 1;
        ctx.strokeStyle = 'rgba(51, 65, 85, 0.3)';
        ctx.stroke();

        animationRef.current = requestAnimationFrame(draw);
        return;
      }

      // --- CONNECTED STATE ---
      // Check output audio level (AIDA speaking)
      const bufferLength = analyser.frequencyBinCount;
      if (dataArray.length !== bufferLength) {
        dataArray = new Uint8Array(bufferLength);
      }
      analyser.getByteFrequencyData(dataArray);
      const outputSum = dataArray.reduce((a, b) => a + b, 0);
      const outputLevel = outputSum / dataArray.length / 255;
      const aidaSpeaking = outputLevel > 0.02;
      const userSpeaking = inputLevel > USER_INPUT_THRESHOLD && !aidaSpeaking;

      if (aidaSpeaking) {
        // --- AIDA SPEAKING: Cyan radial bars ---
        const gradient = ctx.createLinearGradient(0, 0, width, height);
        gradient.addColorStop(0, CYAN);
        gradient.addColorStop(0.5, CYAN);
        gradient.addColorStop(1, BLUE);

        const usefulBufferLength = Math.floor(dataArray.length * 0.6);
        const barsToDraw = 40;
        const step = Math.floor(usefulBufferLength / barsToDraw);
        drawMirroredBars(
          centerX,
          centerY,
          baseRadius,
          maxBarHeight,
          barsToDraw,
          (index) => dataArray[index * step] / 255,
          gradient,
          CYAN,
          15
        );

        // Base ring
        ctx.beginPath();
        ctx.arc(centerX, centerY, baseRadius - 5, 0, 2 * Math.PI);
        ctx.lineWidth = 1;
        ctx.strokeStyle = CYAN_DIM;
        ctx.shadowBlur = 0;
        ctx.stroke();
      } else {
        // --- ASSISTANT LISTENING: Gentle breathing cyan ring ---
        const time = Date.now() * 0.002;
        const breathe = Math.sin(time) * 3;
        const readyRadius = baseRadius + breathe;

        ctx.beginPath();
        ctx.arc(centerX, centerY, readyRadius, 0, 2 * Math.PI);
        ctx.lineWidth = 2;
        ctx.strokeStyle = CYAN;
        ctx.shadowBlur = isMobile ? 0 : 6;
        ctx.shadowColor = CYAN;
        ctx.stroke();

        ctx.beginPath();
        ctx.arc(centerX, centerY, readyRadius - 8, 0, 2 * Math.PI);
        ctx.lineWidth = 1;
        ctx.strokeStyle = 'rgba(6, 182, 212, 0.3)';
        ctx.shadowBlur = 0;
        ctx.stroke();
      }

      // --- USER INPUT OVERLAY: Emerald waveform line inside assistant circle ---
      const time = timestamp * 0.006;
      const normalizedInput = Math.max(0, (inputLevel - USER_INPUT_THRESHOLD) / 0.10);
      const inputStrength = Math.min(0.7, 0.22 + normalizedInput * 0.8);
      const waveformWidth = minDimension * 0.3;
      const baselineY = centerY;
      const pointCount = 17;
      const idleAmplitude = minDimension * 0.006;
      const activeAmplitude = minDimension * 0.055;
      const amplitude = userSpeaking
        ? idleAmplitude + (inputStrength * activeAmplitude)
        : idleAmplitude;
      const startX = centerX - waveformWidth / 2;
      const stepX = waveformWidth / (pointCount - 1);

      ctx.beginPath();
      for (let i = 0; i < pointCount; i++) {
        const x = startX + i * stepX;
        const y = (() => {
          const progress = i / (pointCount - 1);
          const centered = Math.abs(progress - 0.5) * 2;
          const envelope = 1 - Math.pow(centered, 1.35);

          if (userSpeaking) {
            const primary = Math.sin(time * 5 + i * 0.72);
            const secondary = Math.sin(time * 3.2 + i * 0.31 + 0.9) * 0.35;
            return baselineY + ((primary + secondary) * amplitude * envelope);
          }

          const idlePrimary = Math.sin(time * 1.2 + i * 0.18);
          const idleSecondary = Math.sin(time * 0.7 + i * 0.09 + 0.6) * 0.25;
          return baselineY + ((idlePrimary + idleSecondary) * amplitude * envelope);
        })();

        if (i === 0) {
          ctx.moveTo(x, y);
        } else {
          ctx.lineTo(x, y);
        }
      }

      ctx.strokeStyle = EMERALD;
      ctx.lineWidth = userSpeaking ? 3 : 2;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.shadowBlur = isMobile ? 0 : userSpeaking ? 10 : 2;
      ctx.shadowColor = EMERALD;
      ctx.stroke();

      ctx.beginPath();
      ctx.moveTo(startX - 8, baselineY);
      ctx.lineTo(startX + waveformWidth + 8, baselineY);
      ctx.lineWidth = 1;
      ctx.strokeStyle = userSpeaking ? EMERALD_DIM : 'rgba(52, 211, 153, 0.34)';
      ctx.shadowBlur = 0;
      ctx.stroke();

      animationRef.current = requestAnimationFrame(draw);
    };

    animationRef.current = requestAnimationFrame(draw);

    return () => {
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
      window.removeEventListener('resize', resizeCanvas);
    };
  }, [analyser, inputLevel, isConnected, isConnecting, config]);

  return (
    <canvas
      ref={canvasRef}
      className={className}
      style={{ width: '100%', height: config.height }}
    />
  );
};

export default AudioVisualizer;
